<template>
  <div>
    <Message
      v-if="!sellerData?.adstxt_verified"
      class="select-none cursor-pointer"
      :closable="false"
      :severity="sellerData?.adstxt_last_check ? 'error' : 'warn'"
      @click="
        sellerData?.adstxt_last_check
          ? (showAdstxtVerificationResult = true)
          : null
      "
    >
      {{
        sellerData?.adstxt_last_check
          ? "Ads.txt validation failed. Click for more details."
          : "Ads.txt validation has not been done yet"
      }}
    </Message>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Dashboard
    </h2>
    <div
      class="mb-4 flex flex-column sm:flex-row sm:justify-content-between sm:align-items-end gap-4"
    >
      <div class="flex gap-2 align-items-end">
        <Calendar
          v-model="dateRange"
          :showIcon="true"
          :number-of-months="2"
          selectionMode="range"
          @update:modelValue="setDateRange"
        />
        <Button @click="fetchOnDateRange">Fetch Data</Button>
      </div>

      <div class="flex align-items-center gap-2">
        <span class="font-semibold" style="color: var(--text-color)"
          >View:</span
        >
        <SelectButton
          v-model="trafficType"
          :options="trafficOptions"
          optionLabel="label"
          optionValue="value"
        />
      </div>
    </div>

    <DashboardSection
      v-for="section in sections"
      :key="section.dataMode"
      v-bind="section"
      :data="data[section.dataMode]"
      :loading="loading[section.dataMode]"
      :rows="rows"
      :totalRecords="totalRecords[section.dataMode] * 1"
      @fetch-data="fetchData"
      @download-csv="downloadCsv(section.dataMode, section.dataMode + '.csv')"
      @open-schedule-dialog="openScheduleDialog"
    />
  </div>
  <AdstxtMissingLinesModal
    :visible="showAdstxtVerificationResult"
    :missing-lines="sellerData?.adstxt_missing_lines"
    :last-checked="sellerData?.adstxt_last_check"
    @close-modal="showAdstxtVerificationResult = false"
  />
  <AddTableScheduledReportDialog
    :data-mode="scheduleDataMode"
    :visible="scheduleDialogVisible"
    user-role="seller"
    :user-token="scheduleToken"
    @hide="closeScheduleDialog()"
  />
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "axios";
import { API_BASE_URL } from "../consts";
import Calendar from "primevue/calendar";
import SelectButton from "primevue/selectbutton";
import Button from "primevue/button";
import Message from "primevue/message";
import { useStore } from "vuex";
import { adjustDate } from "../helpers";
import DashboardSection from "./DashboardSection.vue";
import AdstxtMissingLinesModal from "./AdstxtValResult/AdstxtMissingLinesModal.vue";
import AddTableScheduledReportDialog from "./AddTableScheduledReportDialog.vue";

export default {
  components: {
    Calendar,
    SelectButton,
    Button,
    Message,
    DashboardSection,
    AdstxtMissingLinesModal,
    AddTableScheduledReportDialog,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const groupAdminMode = route.name == "GroupAdminSellerDashboard";
    const sellerData = store.getters.seller;
    const showAdstxtVerificationResult = ref(false);

    const scheduleDialogVisible = ref(false);
    const scheduleDataMode = ref("");
    const scheduleToken = ref("");

    const sellerId = router.currentRoute.value.params.sellerId;

    const trafficOptions = [
      { label: "Total", value: "all" },
      { label: "Audio", value: "audio" },
      { label: "Video", value: "video" },
    ];
    const trafficType = ref("all");

    const createColumns = (includeGeo = false, includeLoads = true) => {
      const columns = [{ header: "Date", field: "date_trunc" }];

      if (includeLoads)
        columns.push({ header: "Player Loads", field: "loads" });

      columns.push({ header: "Ad Impressions", field: "impressions" });

      columns.push({ header: "Revenue", field: "revenue" });

      if (includeGeo) {
        columns.splice(1, 0, { header: "Geo", field: "geo" });
      }

      if (sellerId && !groupAdminMode) {
        columns.push(
          { header: "Cost", field: "cost" },
          { header: "Profit", field: "profit" }
        );
      }

      columns.push({ header: "ECPM", field: "ecpm" });

      return columns;
    };

    const baseSections = [
      {
        title: "Partner Daily",
        dataMode: "partner_daily",
        columns: createColumns(),
        showChart: true,
      },
      {
        title: "Partner Monthly",
        dataMode: "partner_monthly",
        columns: createColumns(),
        showChart: false,
      },
      {
        title: "Per Geo Location",
        dataMode: "partner_per_geo_daily",
        columns: createColumns(true),
        showChart: false,
        chartLegend: "bottom",
      },
    ];
    const videoSections = [
      {
        title: "Video Daily",
        dataMode: "partner_daily",
        columns: createColumns(false, false),
        showChart: true,
      },
      {
        title: "Video Monthly",
        dataMode: "partner_monthly",
        columns: createColumns(false, false),
        showChart: false,
      },
    ];
    const sections = computed(() =>
      trafficType.value === "video" ? videoSections : baseSections
    );

    const rows = ref(10);
    const totalRecords = reactive({
      partnerDaily: 0,
      partnerMonthly: 0,
      partnerPerDomainDaily: 0,
    });
    const data = reactive({
      partnerDaily: [],
      partnerMonthly: [],
      partnerPerDomainDaily: [],
    });

    const csvData = reactive({
      partnerDaily: [],
      partnerMonthly: [],
      partnerPerDomainDaily: [],
    });

    const loading = reactive({
      partnerDaily: false,
      partnerMonthly: false,
      partnerPerDomainDaily: false,
    });

    const dateRange = ref([
      new Date(new Date().setDate(new Date().getDate() - 7)),
      new Date(),
    ]);

    const openScheduleDialog = ({ dataMode }) => {
      console.log("HEHEHE: ", dataMode);
      scheduleDataMode.value = dataMode;
      scheduleDialogVisible.value = true;
    };

    const closeScheduleDialog = () => {
      scheduleDialogVisible.value = false;
    };

    const setDateRange = (value) => {
      dateRange.value = value;
    };

    const fetchData = async ({
      dataMode,
      page = 0,
      rows = 10,
      sortField,
      sortOrder,
      fetchCsvData = false,
    }) => {
      try {
        loading[dataMode] = true;
        let token = "";

        if (sellerId) {
          if (groupAdminMode) token = localStorage.getItem("groupAdminToken");
          else token = localStorage.getItem("token");
        } else {
          token = localStorage.getItem("sellerToken");
        }

        scheduleToken.value = token;

        const startDate = adjustDate(dateRange.value?.[0]);
        const endDate = adjustDate(dateRange.value?.[1]);

        const params = {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          dataMode: fetchCsvData ? `${dataMode}_all` : dataMode,
          page,
          rows,
          sortField,
          sortOrder,
          sellerId,
          trafficType: trafficType.value,
        };

        const { data: responseData } = await axios.get(
          `${API_BASE_URL}/seller/dashboard`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (responseData.success) {
          if (fetchCsvData) {
            csvData[dataMode] = responseData.data[`${dataMode}`] || [];
          } else {
            data[dataMode] = responseData.data[dataMode] || [];
            totalRecords[dataMode] =
              responseData.data[dataMode]?.[0]?.count || 0;
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (fetchCsvData) {
          csvData[dataMode] = [];
        } else {
          data[dataMode] = [];
          totalRecords[dataMode] = 0;
        }
      } finally {
        loading[dataMode] = false;
      }
    };

    const fetchCsvData = async (dataMode) => {
      await fetchData({ dataMode, fetchCsvData: true });
    };

    const fetchOnDateRange = () => {
      sections.value.forEach((section) => {
        fetchData({ dataMode: section.dataMode });
      });
    };

    onMounted(() => {
      fetchOnDateRange();
    });

    watch(trafficType, fetchOnDateRange);

    const downloadCsv = async (dataMode, filename) => {
      await fetchCsvData(dataMode);
      const fetchedData = csvData[dataMode];
      const headers = Object.keys(fetchedData[0]);
      const csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        fetchedData.map((row) => Object.values(row).join(",")).join("\n");

      console.log("csv", csvContent);
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return {
      sellerData,
      showAdstxtVerificationResult,
      sections,
      rows,
      totalRecords,
      data,
      loading,
      dateRange,
      trafficType,
      trafficOptions,
      setDateRange,
      fetchData,
      fetchOnDateRange,
      downloadCsv,
      scheduleDialogVisible,
      scheduleDataMode,
      scheduleToken,
      openScheduleDialog,
      closeScheduleDialog,
    };
  },
};
</script>
